import { useState, type ReactNode } from "react";
import { apiFetch } from "@contractor-quoting/api-client";
import { Badge, Button, Card } from "@contractor-quoting/shared-ui";

type RecordValue = Record<string, unknown>;

export function ContractorProjectDossier({
  projectId,
  dossier,
}: {
  projectId: string;
  dossier: RecordValue;
}) {
  const overview = (dossier.overview ?? {}) as RecordValue;
  const workItems = (overview.workItems ?? []) as RecordValue[];
  const structuredFacts = (dossier.facts ?? []) as RecordValue[];
  const legacyAnswers: RecordValue[] = ((dossier.answers ?? []) as RecordValue[]).map((answer) => ({
    id: answer.id,
    field: answer.key,
    raw_value: answer.value,
    origin: "customer_statement",
    status: "accepted_for_planning",
  }));
  const facts = [...structuredFacts, ...legacyAnswers];
  const entities = (dossier.entities ?? []) as RecordValue[];
  const measurements = (dossier.measurements ?? []) as RecordValue[];
  const standardDimensionCandidates = (dossier.standardDimensionCandidates ?? []) as RecordValue[];
  const confirmations = (dossier.confirmations ?? []) as RecordValue[];
  const evidence = (dossier.evidence ?? {}) as RecordValue;
  const captures = (evidence.captures ?? []) as RecordValue[];
  const captureTasks = (evidence.captureTasks ?? []) as RecordValue[];
  const observations = (evidence.observations ?? []) as RecordValue[];
  const readiness = (overview.readiness ?? null) as RecordValue | null;
  const calculation = (dossier.calculation ?? {}) as RecordValue;
  const [correctingFactId, setCorrectingFactId] = useState<string | null>(null);
  const [requestingInformation, setRequestingInformation] = useState(false);

  if (!dossier.overview) return null;
  const assumptions = facts.filter((fact) => fact.assumption === true);
  const conflicts = facts.filter((fact) => fact.status === "conflicted");
  const materialFacts = facts.filter((fact) =>
    String(fact.field ?? "").match(/material|finish|style|tier|profile/i),
  );
  const siteFacts = facts.filter((fact) =>
    String(fact.field ?? "").match(/access|level|condition|disposal|obstruction|moisture/i),
  );

  const correctFact = async (fact: RecordValue) => {
    const current = formatValue(fact.normalized_value ?? fact.raw_value);
    const corrected = window.prompt(`Correct ${humanize(String(fact.field))}`, current);
    if (corrected === null || corrected.trim() === "") return;
    const factId = String(fact.id);
    setCorrectingFactId(factId);
    try {
      await apiFetch(`/v2/contractor/projects/${projectId}/facts/${factId}/correct`, {
        method: "POST",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({
          value: corrected,
          normalizedValue: corrected,
          unit: fact.unit || undefined,
          note: "Corrected from contractor project dossier",
        }),
      });
      window.location.reload();
    } finally {
      setCorrectingFactId(null);
    }
  };

  const requestInformation = async () => {
    const instruction = window.prompt(
      "What should the customer provide?",
      "Please provide the missing project detail or measurement.",
    );
    if (!instruction?.trim()) return;
    setRequestingInformation(true);
    try {
      await apiFetch(`/v2/contractor/projects/${projectId}/information-requests`, {
        method: "POST",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({ instruction, requestType: "question" }),
      });
      window.alert("The information request is now the intake agent's next priority.");
    } finally {
      setRequestingInformation(false);
    }
  };

  const correctProjectType = async (workItem: RecordValue) => {
    const projectType = window.prompt(
      "Enter the corrected canonical project type",
      String(workItem.canonical_type),
    );
    if (!projectType?.trim() || projectType === workItem.canonical_type) return;
    const note = window.prompt("Why is this classification changing?", "Contractor review");
    if (!note?.trim()) return;
    await apiFetch(`/v2/contractor/projects/${projectId}/classifications/correct`, {
      method: "POST",
      body: JSON.stringify({
        workItemId: workItem.id,
        projectType: projectType.trim(),
        note,
      }),
    });
    window.location.reload();
  };

  return (
    <>
      <Card className="lg:col-span-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-black">Project dossier</h2>
            <p className="mt-2 max-w-3xl text-stone-600">
              {String(overview.originalDescription || "No original description was recorded.")}
            </p>
          </div>
          {readiness ? (
            <Badge>
              {readiness.contractor_review_ready ? "Ready for review" : "Intake incomplete"}
            </Badge>
          ) : (
            <Badge>Readiness not evaluated</Badge>
          )}
        </div>
        <Button
          className="mt-4"
          disabled={requestingInformation}
          onClick={() => void requestInformation()}
        >
          Request more information
        </Button>
        <div className="mt-5 flex flex-wrap gap-2">
          {workItems.map((item) => (
            <div className="flex items-center gap-1" key={String(item.id)}>
              <Badge>
                {humanize(String(item.canonical_type))} · {humanize(String(item.status))}
              </Badge>
              <button
                className="text-xs font-bold text-emerald-800 underline"
                onClick={() => void correctProjectType(item)}
                type="button"
              >
                Correct
              </button>
            </div>
          ))}
        </div>
      </Card>

      <DossierList
        title="Scope and requirements"
        empty="No structured project facts have been accepted yet."
        items={facts}
        render={(fact) => (
          <>
            <div>
              <p className="font-bold">{humanize(String(fact.field))}</p>
              <p className="text-sm text-stone-500">
                {humanize(String(fact.origin))} · {humanize(String(fact.status))}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                Customer confirmed: {fact.customer_confirmed ? "Yes" : "No"} · Contractor verified:{" "}
                {fact.contractor_confirmed ? "Yes" : "No"}
                {fact.semantic_confidence != null
                  ? ` · Confidence ${Math.round(Number(fact.semantic_confidence) * 100)}%`
                  : ""}
              </p>
            </div>
            <div className="text-right">
              <p className="font-semibold">
                {formatValue(fact.normalized_value ?? fact.raw_value)}
              </p>
              {fact.unit ? <p className="text-xs text-stone-500">{String(fact.unit)}</p> : null}
              {structuredFacts.includes(fact) ? (
                <Button
                  className="mt-2"
                  disabled={correctingFactId === String(fact.id)}
                  onClick={() => void correctFact(fact)}
                >
                  Correct
                </Button>
              ) : null}
            </div>
          </>
        )}
      />

      <DossierList
        title="Measurements"
        empty="No evidence-backed measurements have been recorded."
        items={measurements}
        render={(measurement) => (
          <>
            <div>
              <p className="font-bold">{humanize(String(measurement.measurement_type))}</p>
              <p className="text-sm text-stone-500">
                {humanize(String(measurement.source))} · {humanize(String(measurement.status))}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                Uncertainty:{" "}
                {measurement.uncertainty != null
                  ? `±${String(measurement.uncertainty)}`
                  : "Not reported"}{" "}
                · Customer confirmed: {measurement.customer_confirmed ? "Yes" : "No"} · Contractor
                verified: {measurement.contractor_confirmed ? "Yes" : "No"}
              </p>
            </div>
            <div className="text-right">
              <p className="font-semibold">
                Raw: {String(measurement.raw_value)} {String(measurement.raw_unit)}
              </p>
              <p className="text-sm text-stone-600">
                Normalized: {String(measurement.normalized_value ?? "Not normalized")}{" "}
                {String(measurement.normalized_unit ?? "")}
              </p>
              {(measurement.normalization as RecordValue | null)?.applied ? (
                <p className="mt-1 text-xs font-semibold text-amber-700">
                  Customer-confirmed standard-size planning approximation
                </p>
              ) : null}
            </div>
          </>
        )}
      />

      <DossierList
        title="Standard-size candidates"
        empty="No product-dimension normalization has been proposed."
        items={standardDimensionCandidates}
        render={(candidate) => (
          <>
            <div>
              <p className="font-bold">
                {formatValue(candidate.nominal_dimensions)} {String(candidate.unit)}
              </p>
              <p className="text-sm text-stone-500">
                Raw: {formatValue(candidate.raw_dimensions)} · {humanize(String(candidate.status))}
              </p>
            </div>
            <Badge>{candidate.customer_confirmed ? "Customer confirmed" : "Not confirmed"}</Badge>
          </>
        )}
      />

      <DossierList
        title="Project entities"
        empty="No rooms, openings, fixtures, or other project entities have been recorded."
        items={entities}
        render={(entity) => (
          <>
            <div>
              <p className="font-bold">{String(entity.label)}</p>
              <p className="text-sm text-stone-500">{humanize(String(entity.entity_type))}</p>
            </div>
            {entity.quantity ? <Badge>× {String(entity.quantity)}</Badge> : null}
          </>
        )}
      />

      <DossierList
        title="Materials and preferences"
        empty="No material or finish preferences have been recorded."
        items={materialFacts}
        render={(fact) => (
          <>
            <p className="font-bold">{humanize(String(fact.field))}</p>
            <p>{formatValue(fact.normalized_value ?? fact.raw_value)}</p>
          </>
        )}
      />

      <DossierList
        title="Site conditions"
        empty="No site-condition details have been recorded."
        items={siteFacts}
        render={(fact) => (
          <>
            <p className="font-bold">{humanize(String(fact.field))}</p>
            <p>{formatValue(fact.normalized_value ?? fact.raw_value)}</p>
          </>
        )}
      />

      <DossierList
        title="Evidence and observations"
        empty="No project captures or model observations have been recorded."
        items={[
          ...captureTasks.map((task) => ({ ...task, dossier_kind: "capture_task" })),
          ...captures.map((capture) => ({ ...capture, dossier_kind: "capture" })),
          ...observations.map((observation) => ({ ...observation, dossier_kind: "observation" })),
        ]}
        render={(item) =>
          item.dossier_kind === "capture_task" ? (
            <>
              <div>
                <p className="font-bold">Requested {humanize(String(item.capture_type))}</p>
                <p className="mt-1 text-sm text-stone-600">{String(item.instruction)}</p>
              </div>
              <Badge>{humanize(String(item.status))}</Badge>
            </>
          ) : item.dossier_kind === "capture" ? (
            <>
              <div>
                <p className="font-bold">{String(item.original_filename)}</p>
                <p className="text-sm text-stone-500">
                  {humanize(String(item.capture_type))} · Quality{" "}
                  {humanize(String(item.quality_status))} · Analysis{" "}
                  {humanize(String(item.analysis_status))}
                </p>
              </div>
              <a
                className="font-bold text-emerald-800 underline"
                href={String(item.readUrl)}
                rel="noreferrer"
                target="_blank"
              >
                View private capture
              </a>
            </>
          ) : (
            <>
              <div>
                <p className="font-bold">{humanize(String(item.field))}</p>
                <p className="text-sm text-stone-500">
                  {humanize(String(item.status))} · Confidence{" "}
                  {Math.round(Number(item.semantic_confidence) * 100)}%
                </p>
              </div>
              <p>{formatValue(item.proposed_value)}</p>
            </>
          )
        }
      />

      <Card>
        <h2 className="text-xl font-bold">Assumptions, warnings, and conflicts</h2>
        <p className="mt-3 text-sm text-stone-600">
          {assumptions.length} assumption(s) · {conflicts.length} conflict(s) ·{" "}
          {measurements.length} evidence-backed measurement(s)
        </p>
        {readiness ? (
          <div className="mt-3 grid gap-2 text-sm">
            <IssueList label="Missing" value={readiness.missing_requirements} />
            <IssueList label="Conflicted" value={readiness.conflicted_requirements} />
            <IssueList label="Warnings" value={readiness.warnings} />
          </div>
        ) : null}
      </Card>

      <Card>
        <h2 className="text-xl font-bold">Calculation snapshot</h2>
        <p className="mt-3 text-stone-600">
          {((calculation.snapshots ?? []) as unknown[]).length} calculator input snapshot(s) ·{" "}
          {((calculation.estimates ?? []) as unknown[]).length} estimate version(s)
        </p>
        <p className="mt-2 text-xs text-stone-500">
          Values here come from the deterministic runbook calculation pipeline.
        </p>
        {((calculation.snapshots ?? []) as RecordValue[]).map((snapshot) => (
          <div className="mt-3 rounded-xl bg-stone-100 p-3 text-sm" key={String(snapshot.id)}>
            <p className="font-bold">
              {String(snapshot.adapter_version)} · {String(snapshot.input_hash).slice(0, 12)}
            </p>
            <p className="mt-1 text-stone-600">
              Assumptions: {Array.isArray(snapshot.assumptions) ? snapshot.assumptions.length : 0} ·
              Warnings: {Array.isArray(snapshot.warnings) ? snapshot.warnings.length : 0}
            </p>
          </div>
        ))}
      </Card>

      <Card>
        <h2 className="text-xl font-bold">Review and confirmations</h2>
        <p className="mt-3 text-stone-600">{confirmations.length} recorded decision(s)</p>
      </Card>
    </>
  );
}

function DossierList({
  title,
  empty,
  items,
  render,
}: {
  title: string;
  empty: string;
  items: RecordValue[];
  render: (item: RecordValue) => ReactNode;
}) {
  return (
    <Card>
      <h2 className="text-xl font-bold">{title}</h2>
      {items.length === 0 ? (
        <p className="mt-3 text-stone-600">{empty}</p>
      ) : (
        <div className="mt-3 divide-y">
          {items.map((item, index) => (
            <div
              className="flex items-start justify-between gap-4 py-3"
              key={String(item.id ?? index)}
            >
              {render(item)}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function IssueList({ label, value }: { label: string; value: unknown }) {
  const items = Array.isArray(value) ? value : [];
  if (items.length === 0) return null;
  return (
    <p>
      <strong>{label}:</strong> {items.map(String).join(", ")}
    </p>
  );
}

function humanize(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "Not provided";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}
